// WinBolo sprite-sheet bindings.
//
// tiles.bmp is the canonical 496x176 atlas from John Morrison's WinBolo
// (winbolo/src/gui/win32/tiles.bmp). Each sprite is 16x16 px at fixed
// (x, y) offsets pulled from winbolo/src/gui/tiles.h.
//
// On load we color-key the pixel sampled from the corner of TANK_SELF_0
// to alpha 0 so tank / pillbox / boat sprites composite cleanly over
// terrain. Terrain tiles are fully opaque so the keying doesn't alter
// them; if the key colour ever happens to match a terrain pixel we'd
// see a hole, which we'd address by switching to per-sprite keying.
//
// Vite bundles the BMP through the `?url` import so it ships from the
// same origin as the JS bundle.
import tilesUrl from "../assets/tiles.bmp?url";
import tiles48Url from "../assets/tiles-48.png?url";
import { Terrain } from "@webolo/shared";

// Single canonical sprite per terrain. Multi-tile variants (corners,
// edges, ends) are a separate concern (Phase 5e).
export const TERRAIN_TILE: Readonly<Partial<Record<Terrain, readonly [number, number]>>> = {
  [Terrain.DeepSea]:       [0,   16],
  [Terrain.River]:         [16,  16],
  [Terrain.Grass]:         [32,  16],
  [Terrain.Forest]:        [48,  16],
  [Terrain.Rubble]:        [64,  16],
  [Terrain.Crater]:        [80,  16],
  [Terrain.Swamp]:         [112, 16],
  [Terrain.Wall]:          [272, 16],   // BUILD_SOLID
  [Terrain.HalfBuiltWall]: [352, 32],   // BUILD_MOST1
  [Terrain.Boat]:          [288, 48],   // BOAT_0
  [Terrain.Base]:          [256, 64],   // BASE_NEUTRAL
  [Terrain.Pillbox]:       [144, 16],   // PILL_EVIL_15 (will be specialized below)
  [Terrain.Road]:          [160, 0],    // ROAD_SOLID
};

// Base sprites. Three ownership skins: "good" (friendly), "neutral", and "evil" (enemy).
export const BASE_GOOD:    readonly [number, number] = [160, 16];
export const BASE_NEUTRAL: readonly [number, number] = [256, 64];
export const BASE_EVIL:    readonly [number, number] = [112, 144];

// Pillbox sprites by armor (0..15). Two ownership skins; a "good"
// (friendly) and an "evil" (enemy) version.
export const PILL_EVIL: readonly (readonly [number, number])[] = [
  [0, 80], [16, 80], [32, 80], [48, 80], [64, 80], [80, 80], [96, 80], [112, 80],
  [128, 80], [144, 80], [160, 80], [176, 80], [192, 80], [208, 80], [224, 80], [144, 16],
];
export const PILL_GOOD: readonly (readonly [number, number])[] = [
  [0, 112], [16, 112], [32, 112], [48, 112], [64, 112], [80, 112], [96, 112], [112, 112],
  [128, 112], [144, 112], [160, 112], [176, 112], [192, 112], [208, 112], [224, 112], [240, 112],
];

// Tank sprites by direction (0..15). Self is the local player; Good is
// allied (same team); Evil is hostile. Boat-suffixed variants render
// when the tank is currently sailing.
export const TANK_SELF: readonly (readonly [number, number])[] = [
  [0, 64], [16, 64], [32, 64], [48, 64], [64, 64], [80, 64], [96, 64], [112, 64],
  [128, 64], [144, 64], [160, 64], [176, 64], [192, 64], [208, 64], [224, 64], [240, 64],
];
export const TANK_SELFBOAT: readonly (readonly [number, number])[] = [
  [240, 80], [256, 80], [272, 80], [288, 80], [304, 80], [0, 96], [16, 96], [32, 96],
  [48, 96], [64, 96], [80, 96], [96, 96], [112, 96], [128, 96], [144, 96], [160, 96],
];
export const TANK_GOOD: readonly (readonly [number, number])[] = [
  [304, 98], [320, 98], [336, 98], [96, 144], [352, 98], [368, 98], [384, 98], [400, 98],
  [416, 98], [432, 98], [448, 98], [464, 98], [480, 98], [272, 114], [288, 114], [304, 114],
];
export const TANK_GOODBOAT: readonly (readonly [number, number])[] = [
  [319, 114], [335, 114], [351, 114], [367, 114], [383, 114], [399, 114], [415, 114], [431, 114],
  [447, 114], [463, 114], [479, 114], [0, 128], [16, 128], [32, 128], [48, 128], [64, 128],
];
export const TANK_EVIL: readonly (readonly [number, number])[] = [
  [80, 128], [96, 128], [112, 128], [128, 128], [144, 128], [160, 128], [176, 128], [192, 128],
  [208, 128], [224, 128], [240, 128], [256, 128], [272, 130], [288, 130], [304, 130], [320, 130],
];
export const TANK_EVILBOAT: readonly (readonly [number, number])[] = [
  [336, 130], [352, 130], [368, 130], [384, 130], [400, 130], [416, 130], [432, 130], [448, 130],
  [464, 130], [480, 130], [0, 144], [16, 144], [32, 144], [48, 144], [64, 144], [80, 144],
];

// Shell sprites (16 directions). Unlike everything else in the sheet,
// shells are not 16x16 — each direction has its own (x, y, w, h) so the
// projectile aligns visually to the firing angle.
export interface ShellSprite { x: number; y: number; w: number; h: number; }
export const SHELL_SPRITES: readonly ShellSprite[] = [
  { x: 452, y: 72, w: 3, h: 4 },
  { x: 455, y: 72, w: 3, h: 4 },
  { x: 458, y: 72, w: 4, h: 4 },
  { x: 452, y: 76, w: 4, h: 3 },
  { x: 456, y: 76, w: 4, h: 3 },
  { x: 460, y: 76, w: 4, h: 3 },
  { x: 452, y: 79, w: 4, h: 4 },
  { x: 456, y: 79, w: 3, h: 4 },
  { x: 459, y: 79, w: 3, h: 4 },
  { x: 452, y: 83, w: 3, h: 4 },
  { x: 456, y: 83, w: 3, h: 4 },
  { x: 459, y: 83, w: 4, h: 3 },
  { x: 452, y: 87, w: 4, h: 3 },
  { x: 456, y: 87, w: 4, h: 3 },
  { x: 452, y: 90, w: 4, h: 4 },
  { x: 456, y: 90, w: 4, h: 3 },
];

// Shell impact / tank-killed explosion frames. 16x16 each; played in
// order, one full pass and gone. Frame timing tuned in renderer.
export const EXPLOSION_FRAMES: readonly (readonly [number, number])[] = [
  [464, 48], [480, 48], [464, 64], [480, 64],
  [464, 80], [480, 80], [288, 64], [304, 64],
];

// Boat terrain orientation sprites used by variantForBoat. Indices
// 0..7 match BOAT_0..BOAT_7 in tilenum.h.
export const BOAT_ORIENTATION: readonly (readonly [number, number])[] = [
  [288, 48], [176, 96], [192, 96], [208, 96],
  [224, 96], [240, 96], [256, 96], [272, 96],
];

// Mine glyph (single 16x16 sprite).
export const MINE_SPRITE: readonly [number, number] = [304, 48];

// Gunsight crosshair sprite. tiles.h: GUNSIGHT_X = 17*TILE_SIZE, GUNSIGHT_Y = 4*TILE_SIZE.
export const GUNSIGHT_SPRITE: readonly [number, number] = [272, 64];

// LGM ("little green man" — builder) walking frames. These are tiny
// 3-px-wide sprites packed tightly at y=90 with height 8. The
// renderer upscales them so they're visible at game scale.
export interface LgmFrame { x: number; y: number; w: number; h: number; }
export const LGM_FRAMES: readonly LgmFrame[] = [
  { x: 431, y: 90, w: 3, h: 8 }, // LGM0
  { x: 428, y: 90, w: 3, h: 8 }, // LGM1 (left foot forward)
  { x: 434, y: 90, w: 3, h: 8 }, // LGM2 (right foot forward)
];

// WinBolo's runtime sprite numbering (TANK_SELF_0..15) is 0=North, CW —
// the same convention we use for Tank.dir. The file format's start
// direction byte uses 0=East, CCW, but that conversion is done at parse
// time in winboloMap.ts so by the time we render, dir is already in our
// CW-from-north space.
//
// We round here because lerpDir produces fractional dirs during
// rotation; without rounding the indexer returns undefined for
// non-integer keys and the sprite flashes back to the (0, 0) fallback.
export function dirToSpriteIndex(d: number): number {
  const r = Math.round(d);
  return ((r % 16) + 16) % 16;
}

let cached: HTMLCanvasElement | null = null;
let loading: Promise<HTMLCanvasElement> | null = null;

export function getSpriteSheet(): HTMLCanvasElement | null {
  return cached;
}

export function loadSpriteSheet(): Promise<HTMLCanvasElement> {
  if (cached) return Promise.resolve(cached);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        const ctx = c.getContext("2d");
        if (!ctx) {
          reject(new Error("2d context unavailable"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        // Sample (0, 64) — corner of TANK_SELF_0 — as the transparent
        // color key. WinBolo's sprite sheet uses a single key colour for
        // all sprite borders.
        const data = ctx.getImageData(0, 0, c.width, c.height);
        const px = data.data;
        const ki = (64 * c.width + 0) * 4;
        const kr = px[ki], kg = px[ki + 1], kb = px[ki + 2];
        for (let i = 0; i < px.length; i += 4) {
          if (px[i] === kr && px[i + 1] === kg && px[i + 2] === kb) {
            px[i + 3] = 0;
          }
        }
        ctx.putImageData(data, 0, 0);
        cached = c;
        resolve(c);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => reject(new Error("failed to load tiles.bmp"));
    img.src = tilesUrl;
  });
  return loading;
}

// ---------- Modern (48 px) sheet ----------
// tiles-48.png is a 1488×528 RGBA PNG — every cell is 48×48, same grid
// layout as tiles.bmp but 3× resolution with true alpha (no color key).

let cached48: HTMLCanvasElement | null = null;
let loading48: Promise<HTMLCanvasElement> | null = null;

export function getModernSheet(): HTMLCanvasElement | null {
  return cached48;
}

export function loadModernSheet(): Promise<HTMLCanvasElement> {
  if (cached48) return Promise.resolve(cached48);
  if (loading48) return loading48;
  loading48 = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        const ctx = c.getContext("2d");
        if (!ctx) { reject(new Error("2d context unavailable")); return; }
        // True RGBA — no color-key pass needed.
        ctx.drawImage(img, 0, 0);
        cached48 = c;
        resolve(c);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => reject(new Error("failed to load tiles-48.png"));
    img.src = tiles48Url;
  });
  return loading48;
}
